import { useEffect, useState } from "react";
import { AvatarFace } from "./AvatarFace";
import { nameColorFor } from "./seed";
import { NAME_COLORS, palette } from "./tokens";
import { t } from "../locale";

/**
 * Announces who a decision is being written for.
 *
 * The target was previously a 34px avatar and a line of small caps in a header
 * — the single most important fact on the writing screen, and the easiest thing
 * on it to miss. This states it in the middle of the screen first, then flies
 * it up into the header position it will occupy for the rest of the decision,
 * so the header afterwards reads as *that thing, parked* rather than as chrome
 * the player never looked at.
 *
 * Reduced motion skips straight to the parked header — the announcement is
 * emphasis, never the only place the target is named.
 */

const CENTRE_MS = 1_760;
const FLIGHT_MS = 840;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

export const TargetIntro = ({
  seed,
  name,
  onDone,
}: {
  seed: string;
  name: string;
  onDone: () => void;
}) => {
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) {
      onDone();
      return;
    }
    const fly = setTimeout(() => setDocked(true), CENTRE_MS);
    const done = setTimeout(onDone, CENTRE_MS + FLIGHT_MS);
    return () => {
      clearTimeout(fly);
      clearTimeout(done);
    };
  }, [onDone]);

  return (
    <div
      className="pointer-events-none absolute inset-0 z-40 flex justify-center transition-all"
      style={{
        // Scrim only while it holds the middle, so the writing screen behind it
        // is not dimmed for the whole flight.
        backgroundColor: docked ? "transparent" : `${palette.bg}E6`,
        alignItems: docked ? "flex-start" : "center",
        transitionDuration: `${FLIGHT_MS}ms`,
      }}
    >
      <div
        className="flex flex-col items-center gap-3 transition-all"
        style={{
          transform: docked ? "scale(0.42)" : "scale(1)",
          opacity: docked ? 0 : 1,
          paddingTop: docked ? 8 : 0,
          transitionDuration: `${FLIGHT_MS}ms`,
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <span className="text-sm font-bold uppercase tracking-widest text-ink-dim">
          {t.setup.targetIntro}
        </span>
        <AvatarFace seed={seed} size={96} />
        <span
          className="text-3xl font-bold uppercase tracking-tight"
          style={{ color: nameColorFor(seed, NAME_COLORS) }}
        >
          {name}
        </span>
      </div>
    </div>
  );
};
